import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Music,
  RotateCcw,
  Save,
  ShieldAlert,
  Trash2,
  UserCircle2,
} from "lucide-react";
import { artistsMgmtApi } from "./api";
import { GenresInput } from "./GenresInput";
import type { ArtistaCatalog, ArtistProfileInput } from "./types";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { Can } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

/**
 * Ficha de detalle del artista. Muestra:
 *  - Info de cuenta + badges.
 *  - Actividad (ofertas de booking, apariciones en Line-Up).
 *  - Editor del perfil de catálogo (nombre, géneros, bio, geo, links).
 *  - Flags editables (isBlocked, isArtist).
 *  - Zona de peligro: soft-delete / restore.
 *
 * Espejo acotado de ProducerDetailScreen: sin clubs/eventos/comisión/
 * hard-delete (un artista no es dueño de nada económico).
 */

const COUNTRIES: { value: string; label: string }[] = [
  { value: "", label: "Sin especificar" },
  { value: "CL", label: "Chile" },
  { value: "AR", label: "Argentina" },
  { value: "PE", label: "Perú" },
  { value: "CO", label: "Colombia" },
  { value: "MX", label: "México" },
  { value: "UY", label: "Uruguay" },
  { value: "BR", label: "Brasil" },
  { value: "ES", label: "España" },
  { value: "US", label: "Estados Unidos" },
];

function socialUrl(artista: ArtistaCatalog | null, platform: string): string {
  if (!artista?.redes_sociales) return "";
  const found = artista.redes_sociales.find(
    (r) => r && String(r.platform).toLowerCase() === platform,
  );
  return found?.url || "";
}

export function ArtistDetailScreen() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { push } = useToast();

  const query = useQuery({
    queryKey: ["artists-mgmt", "detail", id],
    queryFn: () => artistsMgmtApi.get(id),
    enabled: Number.isFinite(id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["artists-mgmt", "detail", id] });
    qc.invalidateQueries({ queryKey: ["artists-mgmt", "list"] });
  };

  const update = useMutation({
    mutationFn: (payload: {
      isBlocked?: boolean;
      isArtist?: boolean;
      profile?: ArtistProfileInput;
    }) => artistsMgmtApi.update(id, payload),
    onSuccess: () => {
      push({ kind: "success", message: "Artista actualizado." });
      invalidate();
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message: err?.response?.data?.error || "Error al actualizar.",
      }),
  });

  const softDelete = useMutation({
    mutationFn: () => artistsMgmtApi.softDelete(id),
    onSuccess: () => {
      push({ kind: "success", message: "Artista borrado (soft)." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "Error en soft-delete." }),
  });

  const restore = useMutation({
    mutationFn: () => artistsMgmtApi.restore(id),
    onSuccess: () => {
      push({ kind: "success", message: "Artista restaurado." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "Error al restaurar." }),
  });

  const handleToggleBlocked = (current: boolean) =>
    update.mutate({ isBlocked: !current });
  const handleToggleArtist = (current: boolean) =>
    update.mutate({ isArtist: !current });

  const handleSoftDelete = async () => {
    const ok = await confirmAction({
      title: "Borrar artista (soft, reversible)",
      message:
        "La cuenta desaparece de los listados normales pero se puede restaurar. No borra su perfil de catálogo ni su historial.",
      confirmLabel: "Borrar (soft)",
      tone: "danger",
    });
    if (ok) softDelete.mutate();
  };

  if (query.isLoading) return <LoadingBlock label="Cargando artista…" />;
  if (query.isError || !query.data)
    return <ErrorBlock onRetry={() => query.refetch()} />;

  const { user, artista, stats } = query.data;
  const isDeleted = !!user.deletedAt;
  const fullName =
    artista?.nombre ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    user.userName ||
    user.email;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <button
          onClick={() => navigate("/artists")}
          className="nx-btn-ghost inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft size={14} /> Volver
        </button>
      </div>

      {/* Identidad */}
      <div className="nx-card p-5">
        <div className="flex items-start gap-4">
          {artista?.imagen || user.img ? (
            <img
              src={artista?.imagen || user.img || ""}
              alt=""
              className="w-16 h-16 rounded-full object-cover border border-border"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-bg-subtle border border-border flex items-center justify-center text-fg-muted">
              <UserCircle2 size={28} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="nx-h1">{fullName}</h1>
              {isDeleted && (
                <span className="nx-badge bg-danger-soft text-danger">Borrado (soft)</span>
              )}
              {user.isBlocked && (
                <span className="nx-badge bg-danger-soft text-danger">Bloqueado</span>
              )}
              {!user.linkedArtistaId && (
                <span className="nx-badge bg-warning-soft text-warning">Sin ficha</span>
              )}
            </div>
            <div className="text-sm text-fg-muted mt-1">{user.email}</div>
            <div className="text-xs text-fg-subtle mt-2">
              ID #{user.id} · Registrado {formatDateTime(user.createdAt)}
              {artista && (artista.city || artista.region || artista.country) && (
                <>
                  {" "}
                  ·{" "}
                  {[artista.city, artista.region, artista.country]
                    .filter(Boolean)
                    .join(", ")}
                </>
              )}
            </div>
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {user.linkedArtistaId && (
                <span className="nx-badge bg-accent-soft text-accent">
                  Artista (id #{user.linkedArtistaId})
                </span>
              )}
              {user.isProducer && (
                <span className="nx-badge bg-accent-soft text-accent">Productor</span>
              )}
              {user.isPartner && (
                <span className="nx-badge bg-accent-soft text-accent">Partner</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actividad */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="nx-card p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-bg-subtle flex items-center justify-center text-fg-muted">
            <Music size={16} />
          </div>
          <div>
            <div className="text-lg font-semibold text-fg tabular-nums">
              {stats.offerCount}
            </div>
            <div className="text-xs text-fg-subtle">Propuestas de booking recibidas</div>
          </div>
        </div>
        <div className="nx-card p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-bg-subtle flex items-center justify-center text-fg-muted">
            <CalendarDays size={16} />
          </div>
          <div>
            <div className="text-lg font-semibold text-fg tabular-nums">
              {stats.eventCount}
            </div>
            <div className="text-xs text-fg-subtle">Apariciones en Line-Up</div>
          </div>
        </div>
      </div>

      {/* Editor de perfil de catálogo */}
      <Can permission={PERMISSIONS.ARTISTS_MGMT_EDIT}>
        <ProfileEditor
          key={`${artista?.id ?? "new"}-${query.dataUpdatedAt}`}
          artista={artista}
          hasLinked={!!user.linkedArtistaId}
          saving={update.isPending}
          onSave={(profile) => update.mutate({ profile })}
        />
      </Can>

      {/* Flags editables */}
      <Can permission={PERMISSIONS.ARTISTS_MGMT_EDIT}>
        <div className="nx-card p-5">
          <h2 className="nx-h2 mb-3">Flags</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <FlagButton
              label="Bloqueado"
              description="Bloquea el acceso al portal."
              active={user.isBlocked}
              onClick={() => handleToggleBlocked(user.isBlocked)}
              activeColor="danger"
            />
            <FlagButton
              label="Es artista"
              description="Aparece en el catálogo y es bookeable."
              active={user.isArtist}
              onClick={() => handleToggleArtist(user.isArtist)}
              activeColor="success"
            />
          </div>
        </div>
      </Can>

      {/* Zona de peligro */}
      <div className="nx-card p-5 border-danger/30 bg-danger-soft/20">
        <h2 className="nx-h2 inline-flex items-center gap-2 mb-2 text-danger">
          <ShieldAlert size={18} /> Zona de peligro
        </h2>
        <p className="text-sm text-fg-muted mb-4">
          El borrado es reversible (soft). La cuenta se oculta de los listados
          pero se puede restaurar; no se pierde su perfil ni su historial.
        </p>
        <div className="flex flex-wrap gap-2">
          {!isDeleted && (
            <Can permission={PERMISSIONS.ARTISTS_MGMT_SOFT_DELETE}>
              <button
                onClick={handleSoftDelete}
                className="nx-btn-secondary inline-flex items-center gap-1.5 text-sm"
              >
                <Trash2 size={14} /> Borrar (soft, reversible)
              </button>
            </Can>
          )}
          {isDeleted && (
            <Can permission={PERMISSIONS.ARTISTS_MGMT_SOFT_DELETE}>
              <button
                onClick={() => restore.mutate()}
                className="nx-btn-primary inline-flex items-center gap-1.5 text-sm"
              >
                <RotateCcw size={14} /> Restaurar
              </button>
            </Can>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Editor del perfil de catálogo ──────────────────────────────────
function ProfileEditor({
  artista,
  hasLinked,
  saving,
  onSave,
}: {
  artista: ArtistaCatalog | null;
  hasLinked: boolean;
  saving: boolean;
  onSave: (profile: ArtistProfileInput) => void;
}) {
  const [artistName, setArtistName] = useState(artista?.nombre || "");
  const [genres, setGenres] = useState<string[]>(artista?.generos || []);
  const [bio, setBio] = useState(artista?.bio || "");
  const [country, setCountry] = useState(artista?.country || "");
  const [region, setRegion] = useState(artista?.region || "");
  const [city, setCity] = useState(artista?.city || "");
  const [spotifyUrl, setSpotifyUrl] = useState(artista?.spotify_url || "");
  const [soundcloudUrl, setSoundcloudUrl] = useState(artista?.soundcloud_url || "");
  const [beatportUrl, setBeatportUrl] = useState(socialUrl(artista, "beatport"));
  const [websiteUrl, setWebsiteUrl] = useState(socialUrl(artista, "website"));
  const [instagramUrl, setInstagramUrl] = useState(socialUrl(artista, "instagram"));

  const canSave = artistName.trim().length >= 2;

  const submit = () => {
    if (!canSave || saving) return;
    onSave({
      artistName: artistName.trim(),
      genres,
      bio: bio.trim(),
      country,
      region: region.trim(),
      city: city.trim(),
      spotifyUrl: spotifyUrl.trim(),
      soundcloudUrl: soundcloudUrl.trim(),
      beatportUrl: beatportUrl.trim(),
      websiteUrl: websiteUrl.trim(),
      instagramUrl: instagramUrl.trim(),
    });
  };

  return (
    <div className="nx-card p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="nx-h2">Perfil de catálogo</h2>
        {!hasLinked && (
          <span className="nx-badge bg-warning-soft text-warning">
            Sin ficha — al guardar se crea
          </span>
        )}
      </div>

      <div>
        <label className="text-2xs text-fg-subtle block mb-1">
          Nombre artístico <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          value={artistName}
          onChange={(e) => setArtistName(e.target.value)}
          className="nx-input w-full"
          placeholder="Alias / nombre de escenario"
        />
      </div>

      <div>
        <label className="text-2xs text-fg-subtle block mb-1">Géneros</label>
        <GenresInput value={genres} onChange={setGenres} />
      </div>

      <div>
        <label className="text-2xs text-fg-subtle block mb-1">Bio</label>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={3}
          className="nx-input w-full resize-y"
          placeholder="Breve descripción del artista."
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-2xs text-fg-subtle block mb-1">País</label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="nx-input w-full"
          >
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-2xs text-fg-subtle block mb-1">Región</label>
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="nx-input w-full"
            placeholder="Región / Estado"
          />
        </div>
        <div>
          <label className="text-2xs text-fg-subtle block mb-1">Ciudad</label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="nx-input w-full"
            placeholder="Ciudad"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-2xs text-fg-subtle block mb-1">Spotify</label>
          <input
            type="text"
            value={spotifyUrl}
            onChange={(e) => setSpotifyUrl(e.target.value)}
            className="nx-input w-full"
            placeholder="open.spotify.com/artist/…"
          />
        </div>
        <div>
          <label className="text-2xs text-fg-subtle block mb-1">SoundCloud</label>
          <input
            type="text"
            value={soundcloudUrl}
            onChange={(e) => setSoundcloudUrl(e.target.value)}
            className="nx-input w-full"
            placeholder="soundcloud.com/…"
          />
        </div>
        <div>
          <label className="text-2xs text-fg-subtle block mb-1">Beatport</label>
          <input
            type="text"
            value={beatportUrl}
            onChange={(e) => setBeatportUrl(e.target.value)}
            className="nx-input w-full"
            placeholder="beatport.com/artist/…"
          />
        </div>
        <div>
          <label className="text-2xs text-fg-subtle block mb-1">Instagram</label>
          <input
            type="text"
            value={instagramUrl}
            onChange={(e) => setInstagramUrl(e.target.value)}
            className="nx-input w-full"
            placeholder="instagram.com/…"
          />
        </div>
      </div>

      <div>
        <label className="text-2xs text-fg-subtle block mb-1">Web</label>
        <input
          type="text"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          className="nx-input w-full"
          placeholder="tusitio.com"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={!canSave || saving}
          className="nx-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          <Save size={14} /> Guardar perfil
        </button>
      </div>
    </div>
  );
}

// ── Botón de flag (copiado de ProducerDetailScreen) ────────────────
function FlagButton({
  label,
  description,
  active,
  onClick,
  activeColor,
}: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
  activeColor: "danger" | "warning" | "success";
}) {
  const colorClass =
    activeColor === "danger"
      ? "border-danger/40 bg-danger-soft text-danger"
      : activeColor === "warning"
        ? "border-warning/40 bg-warning-soft text-warning"
        : "border-success/40 bg-success-soft text-success";

  return (
    <button
      onClick={onClick}
      className={`text-left p-3 rounded-md border transition-colors ${
        active
          ? colorClass
          : "border-border bg-bg-card hover:bg-bg-hover text-fg-muted"
      }`}
    >
      <div className="text-sm font-medium mb-0.5">{label}</div>
      <div className="text-xs opacity-80">{description}</div>
      <div className="text-xs mt-1 font-mono">{active ? "ON" : "OFF"}</div>
    </button>
  );
}
